import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import {
  combineDateWithExistingTime,
  combineTimeWithExistingDate,
  formatTimeForInput,
  parseTimeInput,
} from 'utils/formatters/Date';

// Local calendar Date matching `value`'s day in `timezone`, so the date picker
// shows the correct day regardless of the browser's timezone.
function toCalendarDate(value, timezone) {
  if (!value) return null;
  const m = timezone ? moment.tz(value, timezone) : moment(value);
  if (!m.isValid()) return null;
  return new Date(m.year(), m.month(), m.date());
}

/**
 * The shared date+time field mechanism. The consumer owns a single `value`
 * (a full datetime) and an `onChange`; this hook handles everything in between:
 *
 * - keeps the masked TimePicker's raw-text draft so partial entries
 *   ("04:4_ PM") stay editable instead of snapping back to the last moment,
 * - merges the date and time halves back into one value,
 * - normalizes the calendar day for the date picker across timezones.
 *
 * Returns the props the date and time sub-pickers need. Emits a tz-aware
 * `moment` when `timezone` is set, otherwise a plain `Date` (or `null`).
 */
export default function useDateTimeField({
  value,
  onChange,
  timezone,
  is24Hour = false,
}) {
  const [timeText, setTimeText] = useState(() =>
    formatTimeForInput(value, is24Hour),
  );
  const timeTextRef = useRef(timeText);
  timeTextRef.current = timeText;

  // Mirror the committed value when the parent changes it, but never clobber an
  // in-progress partial entry (one that doesn't yet parse to a complete time).
  useEffect(() => {
    const current = timeTextRef.current;
    if (current === '' || parseTimeInput(current)) {
      const next = formatTimeForInput(value, is24Hour);
      if (next !== current) setTimeText(next);
    }
  }, [value, is24Hour]);

  const toOutput = (combined) => {
    if (!combined) return null;
    return timezone ? moment.tz(combined, timezone) : combined;
  };

  const handleDateChange = (datePart) => {
    const combined = combineDateWithExistingTime(datePart, value, timezone);
    setTimeText(formatTimeForInput(combined, is24Hour));
    onChange(toOutput(combined));
  };

  const handleTimeChange = (e) => {
    const raw = e.target.value;
    // Always echo the raw text so the masked input stays editable; only commit
    // a moment once the typed time is complete and valid.
    setTimeText(raw);
    if (!parseTimeInput(raw)) return;
    const combined = combineTimeWithExistingDate(raw, value, timezone);
    onChange(toOutput(combined));
  };

  return {
    dateValue: toCalendarDate(value, timezone),
    timeText,
    handleDateChange,
    handleTimeChange,
  };
}
